import { core_v1_DataSource } from '@antimatterhq/asl-api-ts-v1';
import { produce, WritableDraft } from 'immer';
import { formatDurationString } from '@antimatterhq/ui/utils/format';
import { deepEqual } from './';
import { AddDataSourceSchemaFormValues } from '@/zod/schemas/dataSource/AddDataSource';

export const getIsGettingStartedCompleted = (
  dataSource: core_v1_DataSource,
) => {
  return (
    !!dataSource.metadata?.name &&
    !!(
      dataSource.spec.schedule?.continuous ||
      dataSource.spec.schedule?.atLeastEvery ||
      dataSource.spec.schedule?.exactly
    )
  );
};

export const getGettingStartedInitialValues = (
  dataSource: core_v1_DataSource,
  overrideEnabled?: boolean,
): AddDataSourceSchemaFormValues => {
  const { spec, metadata } = dataSource;

  const name = metadata?.name ?? '';
  const description = metadata?.comment ?? '';
  const enabled = spec.schedule?.enabled ?? true;
  const continuous = spec.schedule?.continuous ?? false;
  const durationObject = formatDurationString(spec.schedule?.atLeastEvery);
  const duration = durationObject?.value ?? '';
  const durationUnit = durationObject?.unit ?? 'm';
  const cron = spec.schedule?.exactly ?? '';
  const notebook = spec.custom?.notebook ?? '';

  const schedule = continuous ? 'continuous' : cron ? 'cron' : 'duration';

  return {
    name,
    description,
    schedule,
    duration: schedule === 'duration' ? Number(duration || 10) : undefined,
    durationUnit: schedule === 'duration' ? durationUnit : 'm',
    cron: cron || '0 0/5 * * * ?', // default cron expression to 5 minutes
    enabled: overrideEnabled || enabled,
    customNotebookPath: notebook,
  };
};

export const setGettingStartedFormValues = (
  dataSource: core_v1_DataSource,
  values: AddDataSourceSchemaFormValues,
  overrideEnabled?: boolean,
) => {
  const {
    name,
    description: comment,
    duration: atLeastEvery,
    durationUnit,
    cron: exactly,
    schedule,
    customNotebookPath: notebook,
  } = values;

  const enabled = overrideEnabled ? false : values.enabled;

  return produce(
    dataSource,
    (draftState: WritableDraft<core_v1_DataSource>) => {
      if (!draftState.metadata) {
        draftState.metadata = { name: '' };
      }

      draftState.metadata.name = name;
      draftState.metadata.comment = comment;

      switch (schedule) {
        case 'duration':
          draftState.spec.schedule = {
            atLeastEvery: `${atLeastEvery}${durationUnit}`,
          };
          break;
        case 'cron':
          draftState.spec.schedule = {
            exactly,
          };
          break;
        case 'continuous':
          draftState.spec.schedule = {
            continuous: true,
          };
          break;
      }

      draftState.spec.schedule.computeGroup = 'dedicated';
      draftState.spec.schedule.enabled = enabled;

      draftState.spec.custom = notebook
        ? {
            notebook,
          }
        : undefined;

      return draftState;
    },
  );
};

export const gettingStartedHasUnsavedChanges = (
  datasource: core_v1_DataSource,
  unsavedDatasource: core_v1_DataSource | undefined,
) => {
  if (!unsavedDatasource) {
    return false;
  }
  return (
    !deepEqual(datasource.spec.custom, unsavedDatasource.spec.custom) ||
    datasource.metadata?.name !== unsavedDatasource.metadata?.name ||
    datasource.metadata?.comment !== unsavedDatasource.metadata?.comment ||
    !deepEqual(datasource.spec.schedule, unsavedDatasource.spec.schedule)
  );
};
